import React from "react";
import { observable, action, makeObservable } from "mobx";
import { observer } from "mobx-react";
import {GameInfo} from "../../../services/games/game-info";
import {OrbitComponent} from "../../base-components/orbit-component/orbit-component";
import {VideoTileComponent} from "../../live-streaming-tile/video-common-components/video-tile/video-tile.component";
import {VideoItemContainerBox} from "./boxes/video-item-container.box";
import {IVideoPlayingService} from "../../../services/video-live-stream/video-playing.service.interface";

export enum VIDEO_ITEM_TYPE {
 LIVE_STREAM = 'Live-Streaming-Video-Item',
 STATIC = 'Static-Video-Item'
}

export interface SingleGameVideoTemplateProps {
    gameInfo: GameInfo;
    intersectionTarget?: HTMLDivElement;
    onOpenRealGame: () => void;
    videoItemType: VIDEO_ITEM_TYPE;
    isLastGametype?: boolean;
    gameIndex?: number;
    onBringInCentralView?: (gameIndex: number) => void;

}

@observer
export class SingleGameVideoTemplate extends OrbitComponent<SingleGameVideoTemplateProps> {

    constructor(props: SingleGameVideoTemplateProps) {
        super(props);

        makeObservable(this);
    }


    private _inSwiperIntersectionObserver: IntersectionObserver | null = null;
    private _viewportIntersectionObserver: IntersectionObserver | null = null;
    private _videoContainerElement: HTMLDivElement | null = null;
    @observable.ref
    private _isInCenterStage = false;
    @observable.ref
    private _inViewport = false;

    private _captureVideoContainerRef = (element: HTMLDivElement | null) => {
        this._videoContainerElement = element;
        if (!this._inSwiperIntersectionObserver || !this._viewportIntersectionObserver) {
            return;
        }
        if (element) {
            this._inSwiperIntersectionObserver.observe(element);
            this._viewportIntersectionObserver.observe(element);
        } else {
            this._inSwiperIntersectionObserver.unobserve(element);
            this._viewportIntersectionObserver.unobserve(element);
        }
    }


    private _triggerOnBringInCentralView(): void {
        if (this._inViewport && this._isInCenterStage) {
            this.props.onBringInCentralView(this.props.gameIndex);
        }
    }

    private _createSwiperIntersectionObserver(element: HTMLElement) {
        const intersectionObserverOptions: IntersectionObserverInit = {
            threshold: 0.99,
            root: element
        };
        if (this.services.mediaQuery.xlAndAbove) {
            intersectionObserverOptions.rootMargin = "0px -33.33%";
        }

        this._inSwiperIntersectionObserver = new IntersectionObserver(this._inSwiperIntersectionObserverCallback, intersectionObserverOptions);
    }

    private _inSwiperIntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            this.setIsInCenterStage(entry.isIntersecting);
        });
    }

    @action.bound
    setIsInCenterStage(i: boolean) {
        this._isInCenterStage = i;
        this._triggerOnBringInCentralView();
    }

    private _destroySwiperIntersectionObserver() {
        if (this._inSwiperIntersectionObserver) {
            this._inSwiperIntersectionObserver.disconnect();
            this._inSwiperIntersectionObserver = null;
        }
    }

    private _createViewportIntersectionObserver() {
        this._viewportIntersectionObserver = new IntersectionObserver(this._viewportIntersectionObserverCallback, {
            threshold: 0.99
        });
    }

    private _viewportIntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            this.setInViewport(entry.isIntersecting);
        });
    }

    @action.bound
    setInViewport(i: boolean) {
        this._inViewport = i;
        this._triggerOnBringInCentralView();
    }

    private _destroyViewportIntersectionObserver() {
        if (this._viewportIntersectionObserver) {
            this._viewportIntersectionObserver.disconnect();
            this._viewportIntersectionObserver = null;
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this._createViewportIntersectionObserver();
        if (this._videoContainerElement) {
            this._viewportIntersectionObserver.observe(this._videoContainerElement);
        }
        if (this.props.intersectionTarget) {
            this._createSwiperIntersectionObserver(this.props.intersectionTarget);
            if (this._videoContainerElement) {
                this._inSwiperIntersectionObserver.observe(this._videoContainerElement);
            }
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this._destroySwiperIntersectionObserver();
        this._destroyViewportIntersectionObserver();
    }

    componentDidUpdate(prevProps: SingleGameVideoTemplateProps) {
        if (this.props.intersectionTarget) {
            if (this.props.intersectionTarget !== prevProps.intersectionTarget) {
                this._destroySwiperIntersectionObserver();
                this._createSwiperIntersectionObserver(this.props.intersectionTarget);
                this._inSwiperIntersectionObserver.observe(this._videoContainerElement);
            }
        } else {
            this._destroySwiperIntersectionObserver();
        }
    }

    private _getVideoPlayingService = (): IVideoPlayingService => {
        if (this.props.videoItemType === VIDEO_ITEM_TYPE.LIVE_STREAM) {
            return this.services.liveStreamPlaying;
        }

        return this.services.staticVideoPlaying;
    };

    render() {
        return (
            <VideoItemContainerBox
                ref={this._captureVideoContainerRef}
                stepBackStyle={this.services.mediaQuery.xlAndAbove && !this._isInCenterStage}
            >
                <VideoTileComponent
                    gameInfo={this.props.gameInfo}
                    isInCenterStage={this._isInCenterStage}
                    inViewport={this._inViewport}
                    onOpenRealGame={this.props.onOpenRealGame}
                    videoPlayingService={this._getVideoPlayingService()}
                    videoItemType={this.props.videoItemType}
                    isLastGametype={this.props.isLastGametype}
                />
            </VideoItemContainerBox>
        );
    }
}
